import { Tooltip } from 'antd';
import { Zone } from '../../../@types/projects/types';
import { useTheme } from '../../../providers/theme';
import { getColors } from '../../utils/getColors';
import { isInvalidFraction } from '../utils';
import { AnalyzeType } from '../../../@types/samples/types';

type GasOilKeys =
  | `key`
  | `numberOfPoints`
  | `fu${number}Sg`
  | `fu${number}Krg`
  | `fu${number}Krog`
  | `fu${number}Krg (Scaled)`
  | `fu${number}Krog (Scaled)`;

export type WaterOilTablePropData = {
  [key in GasOilKeys]: string;
};

export function getGasOilData(
  zones: Zone[], 
  analysis_type: AnalyzeType,
  showScaledKr: boolean = false
) {
  const maxPoints = Math.max(
    ...zones.map(
      zone =>
        zone.zone_curves?.liao_curves?.sg.length ||
        zone.zone_curves?.lambda_curves?.sg.length ||
        0
    )
  );

  return Array.from({ length: maxPoints }, (_, pointIndex) => {
    const result = {
      key: (pointIndex + 1).toString(),
      numberOfPoints: pointIndex.toString()
    };

    zones.forEach((zone, zoneIndex) => {
      const name = `fu${zoneIndex + 1}`;
      const zoneCurves =
        analysis_type === AnalyzeType.LIAO
          ? zone?.zone_curves?.liao_curves
          : zone?.zone_curves?.lambda_curves;
      const zoneValues =
        analysis_type === AnalyzeType.LIAO
          ? zone?.zone_values?.liao_values
          : zone?.zone_values?.lambda_values;
      const firstKrow = zoneCurves?.krow[0];

      if (zoneCurves && zoneCurves.height[pointIndex] !== undefined) {
        const krgValue = zoneCurves.krg[pointIndex]?.toFixed(4);
        const krogValue = zoneCurves.krog[pointIndex]?.toFixed(4);
        const sgValue = zoneCurves.sg[pointIndex]?.toFixed(4);
        const krgscaled = zoneCurves.krg_scaled[pointIndex]?.toFixed(4);
        const krogscaled = zoneCurves.krog_scaled[pointIndex]?.toFixed(4);

        result[`${name}Sg`] = sgValue;
        result[`${name}Krg (Scaled)`] = krgscaled;
        result[`${name}Krog (Scaled)`] = krogscaled;

        // Change krg and krog values based on showScaledKr if showScaledKr is true
        result[`${name}Krg`] = showScaledKr ? krgscaled : krgValue;
        result[`${name}Krog`] = showScaledKr ? krogscaled : krogValue;

        if (pointIndex === 0) {
          if (krogValue !== undefined && firstKrow !== undefined) {
            result[`${name}KrogColor`] =
              parseFloat(krogValue) === firstKrow ? '#17B169' : 'red';
          }
          if (krgValue !== undefined) {
            result[`${name}KrgColor`] =
              parseFloat(krgValue) === 0.0 ? '#17B169' : 'red';
          }
          if (sgValue !== undefined) {
            result[`${name}SgColor`] =
              parseFloat(sgValue) === 0.0 ? '#17B169' : 'red';
          }
        }

        if (pointIndex === maxPoints - 1) {
          if (krogValue !== undefined) {
            result[`${name}KrogColor`] =
              parseFloat(krogValue) === 0.0 ? '#17B169' : 'red';
          }

          if (sgValue !== undefined) {
            result[`${name}SgColor`] =
              parseFloat(sgValue) ===
              parseFloat(
                (
                  1 -
                  (zoneValues?.swir ?? 0) -
                  (zoneValues?.sor_g ?? 0)
                ).toFixed(4)
              )
                ? '#17B169'
                : 'red';
          }
        }

        if (isInvalidFraction(krgValue)) result[`${name}KrgColor`] = 'red';
        if (isInvalidFraction(krogValue)) result[`${name}KrogColor`] = 'red';
        if (isInvalidFraction(sgValue)) result[`${name}SgColor`] = 'red';
      }
    });

    return result as WaterOilTablePropData;
  });
}

export const GenerateColumns = (zones: Zone[], showScaledKr: boolean = false) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);

  const baseColumns = [
    {
      title: 'Number of Points',
      dataIndex: 'numberOfPoints',
      key: 'numberOfPoints',
      width: 100
    }
  ];

  const zoneColumns = Array.from({ length: zones.length }, (_, index) => {
    const zoneNumber = index + 1;
    const color = zones[index].color || '#fff';

    // Colunas padrão
    const children = [
      {
        title: 'Sg (frac)',
        dataIndex: `fu${zoneNumber}Sg`,
        key: `fu${zoneNumber}Sg`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}SgColor`] || colors.text;
          return (
            <Tooltip
              title={
                color === 'red'
                  ? record.key === '1'
                    ? 'Sg must be = 0'
                    : 'Sg must be = 1-Swir-Sor'
                  : ''
              }
            >
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      },
      {
        title: showScaledKr ? 'Krg scaled (frac)' : 'Krg (frac)',
        dataIndex: `fu${zoneNumber}Krg`,
        key: `fu${zoneNumber}Krg`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}KrgColor`];
          return (
            <Tooltip
              title={
                color === 'red'
                  ? record.key === '1'
                    ? 'Krg must be = 0'
                    : 'Krg must be > 0 and < 1'
                  : ''
              }
            >
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      },
      {
        title: showScaledKr ? 'Krog scaled (frac)' : 'Krog (frac)',
        dataIndex: `fu${zoneNumber}Krog`,
        key: `fu${zoneNumber}Krog`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}KrogColor`] || colors.text;
          return (
            <Tooltip
              title={
                color === 'red'
                  ? record.key === '1'
                    ? 'Krog must be = Krow'
                    : 'Krog must be > 0 and < 1'
                  : ''
              }
            >
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      }
    ];

    // if want to show extra columns (optional)
    // if (showScaledKr) {
    //   children.push(
    //     {
    //       title: 'Krg (Scaled)',
    //       dataIndex: `fu${zoneNumber}Krg (Scaled)`,
    //       key: `fu${zoneNumber}Krg (Scaled)`,
    //       width: 80,
    //       render: (text: string) => (
    //         <Tooltip>
    //           <span>{text}</span>
    //         </Tooltip>
    //       )
    //     },
    //     {
    //       title: 'Krog (Scaled)',
    //       dataIndex: `fu${zoneNumber}Krog (Scaled)`,
    //       key: `fu${zoneNumber}Krog (Scaled)`,
    //       width: 80,
    //       render: (text: string) => (
    //         <Tooltip>
    //           <span>{text}</span>
    //         </Tooltip>
    //       )
    //     }
    //   );
    // }

    return {
      title: <span style={{ color }}>{`RT${zoneNumber}`}</span>,
      children
    };
  });

  return [...baseColumns, ...zoneColumns];
};
